import type { ApolloError } from '@apollo/client';

/**
 * Reads the exception name AppSync attaches to a GraphQL error.
 *
 * AppSync's `util.error(message, errorType)` serialises `errorType` as a
 * TOP-LEVEL field on each error in the GraphQL response — it is NOT placed
 * under `extensions`, and Apollo Client does not fold it in. Reading only
 * `extensions.errorType` therefore never matches a real AppSync response
 * (the classification silently fails and callers fall back to generic error
 * handling). Read the top-level field first, then fall back to
 * `extensions.errorType` for any transport or test double that places it there.
 */
export function getAppSyncErrorType(
  error:
    | {
        errorType?: unknown;
        extensions?: { [key: string]: unknown } | null;
      }
    | undefined,
): string | undefined {
  if (!error) return undefined;
  if (typeof error.errorType === 'string') return error.errorType;
  const fromExtensions = error.extensions?.['errorType'];
  return typeof fromExtensions === 'string' ? fromExtensions : undefined;
}

/**
 * True for AppSync's `ResourceNotFoundException`, read off either failure shape:
 * a thrown `ApolloError`, or one Apollo resolved into a query result.
 */
export function isResourceNotFound(error: unknown): boolean {
  const graphQLErrors = (error as ApolloError | undefined)?.graphQLErrors;
  return (
    graphQLErrors?.some(
      (graphQLError) =>
        getAppSyncErrorType(graphQLError) === 'ResourceNotFoundException',
    ) ?? false
  );
}

/**
 * Reads the structured detail AppSync attaches to a GraphQL error.
 *
 * The counterpart to `getAppSyncErrorType` for `util.error`'s FOURTH argument:
 * AppSync serialises `errorInfo` as a TOP-LEVEL field on each error, so it is read
 * the same way, with the `extensions` fallback covering a transport or test double
 * that places it there.
 *
 * `errorInfo` carries detail that must NOT be folded into the message: a 403 puts
 * `deniedNamespace` here so the refusal sentence stays the service's own prose and
 * the client presents the structured part itself (see `getAccessDeniedNamespace`).
 *
 * Returns an indexable object or `undefined` — never a primitive — so callers can
 * read a field off it without re-checking the type.
 */
export function getAppSyncErrorInfo(
  error:
    | {
        errorInfo?: unknown;
        extensions?: { [key: string]: unknown } | null;
      }
    | undefined,
): Record<string, unknown> | undefined {
  if (!error) return undefined;
  const candidates = [error.errorInfo, error.extensions?.['errorInfo']];
  for (const candidate of candidates) {
    // Arrays are excluded deliberately: `errorInfo` is an envelope keyed by field
    // name, and treating a list as one would silently read `undefined` for every
    // lookup instead of revealing that the shape is wrong.
    if (
      candidate &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate)
    ) {
      return candidate as Record<string, unknown>;
    }
  }
  return undefined;
}
